/**
 * The evidence panel — permanent furniture, not a detail view.
 *
 * It resolves whatever is selected into: the span highlighted inside the canonical
 * block text, the surrounding conversation ordered over the **container** with all
 * senders interleaved, and a provenance strip carrying everything a reader needs to
 * go and check the claim against the original file.
 *
 * The panel takes only a block id and a range. It fetches the text itself, from the
 * server, from the same immutable `blocks.text` the offsets are addressed against.
 * Callers cannot pass it text to display — which is what makes it impossible for a
 * caller to show a quote the evidence spine never agreed to.
 *
 * On a phone it is the same panel and the same permanence, folded: there is no width
 * at which a 26rem column and a readable answer both fit, so the panel becomes a
 * sheet over the answer instead of a column beside it. It still holds one selection,
 * still serves every citing surface, and still cannot be handed text — only where it
 * sits changes.
 */

import { useEffect, useRef, useState } from "react";
import type { Block, ContextMessage } from "../lib/api";
import { api, ApiError } from "../lib/api";
import { describeLocator, isTranscript, transcriptDeepLink } from "../lib/locator";
import { useSheet, useSheetMode } from "../lib/sheet";
import { useEvidence } from "../state/evidence";
import { Marked } from "./Marked";

/** The band in which the panel is neither a column nor a sheet, but an ordinary block
 * further down a scrolling document: wide enough that app.css has released the panes'
 * scrollbars, too narrow to keep three columns. Kept in step with the two `@media`
 * rules there by hand, for the reason recorded on `SHEET` in lib/sheet.ts. */
const INLINE = "(max-width: 1100px)";

export function EvidencePanel({
  datasetId,
  contextWindow = 8,
}: {
  datasetId: string;
  /** ±N messages of container context (design proposal, "±30 messages of container
   * context"): the dossier view widens this from the default 8, everything else keeps
   * it. */
  contextWindow?: number;
}) {
  const { selected, sheetOpen, closeSheet, clear } = useEvidence();
  const scroller = useRef<HTMLElement>(null);
  const sheet = useSheet(scroller, sheetOpen, closeSheet);
  const [block, setBlock] = useState<Block | null>(null);
  const [secondaryBlock, setSecondaryBlock] = useState<Block | null>(null);
  const [context, setContext] = useState<ContextMessage[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selected) {
      setBlock(null);
      setSecondaryBlock(null);
      setContext(null);
      return;
    }
    let cancelled = false;
    setBlock(null);
    setSecondaryBlock(null);
    setContext(null);
    setError(null);

    void api
      .block(datasetId, selected.blockId)
      .then((fetched) => {
        if (!cancelled) setBlock(fetched);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
      });

    // The second half of a pair (design D1/D6 of cooccurrence-graph): a distinct
    // block only when the caller actually gave one, and never the same fetch twice
    // when both mentions happen to share a block.
    if (selected.secondaryBlockId && selected.secondaryBlockId !== selected.blockId) {
      void api
        .block(datasetId, selected.secondaryBlockId)
        .then((fetched) => {
          if (!cancelled) setSecondaryBlock(fetched);
        })
        .catch((cause: unknown) => {
          if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
        });
    }

    void api
      .context(datasetId, selected.blockId, contextWindow)
      .then((messages) => {
        if (!cancelled) setContext(messages);
      })
      .catch((cause: unknown) => {
        // A 404 here is normal and not an error: a PDF paragraph has no conversation.
        // Anything else is worth showing.
        if (!cancelled && !(cause instanceof ApiError && cause.status === 404)) {
          setError(cause instanceof Error ? cause.message : String(cause));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [datasetId, selected, contextWindow]);

  // Scroll back to the top on every new selection. The conversation context can run to
  // dozens of messages, so a panel that kept its previous scroll position opened with
  // the block text and the provenance strip already above the fold — the two things
  // the panel exists to show. Found by looking at it with a real corpus loaded.
  //
  // The middle branch is the one that needs explaining. Between the two breakpoints
  // the panel has no scrollbar of its own: it is a block further down a scrolling
  // document, and `scrollTo` has nothing to move, so instead of scrolling the panel it
  // scrolls the panel *to* the reader. Without it a tap on a marker on a tablet-width
  // screen appears to do nothing at all.
  //
  // That branch used to run on phones too, and was written for them. It no longer
  // does, because it no longer has anything to compensate for: the sheet arrives over
  // the answer under its own power. Narrowed rather than deleted — the document shape
  // it was written for still exists, just not on the screen that prompted it.
  //
  // `block` is in the dependency list, not just `selected`, and that is the whole
  // difference between the middle branch working and not: on selection the panel is
  // one line of "resolving…", so the document is barely taller than the screen and
  // there is nothing to scroll yet. Waiting for the fetched text means scrolling a
  // page that has already grown to hold it.
  useEffect(() => {
    const node = scroller.current;
    if (!node) return;
    if (!sheet && window.matchMedia(INLINE).matches) {
      if (!selected || !block) return;
      node.scrollIntoView({
        block: "start",
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
      return;
    }
    node.scrollTo({ top: 0 });
  }, [selected, block, sheet]);

  if (!selected) {
    return (
      <aside className="evidence" data-testid="evidence-panel">
        <p className="eyebrow">Evidence</p>
        <p data-testid="evidence-empty" style={{ color: "var(--ink-faint)", maxWidth: "28ch" }}>
          Select a citation to see the exact characters it rests on, and where they came
          from.
        </p>
      </aside>
    );
  }

  return (
    <>
      {sheet && sheetOpen && (
        // Tapping past the sheet dismisses it, and while it is there nothing behind it
        // is clickable — which is most of what "modal" has to mean on a touch screen.
        // `aria-hidden` because the close control and Escape already say it to anyone
        // not using a finger.
        <div
          className="sheet-backdrop"
          data-testid="evidence-backdrop"
          aria-hidden="true"
          onClick={closeSheet}
        />
      )}
      <aside
        className={sheetOpen ? "evidence sheet-open" : "evidence"}
        data-testid="evidence-panel"
        ref={scroller}
        tabIndex={-1}
        aria-label="Evidence"
        role={sheet ? "dialog" : undefined}
        aria-modal={sheet ? true : undefined}
      >
        <header className="evidence-head sheet-head">
          <p className="eyebrow">
            Evidence{selected.label ? ` · ${selected.label}` : ""}
            {selected.origin ? ` · from ${selected.origin}` : ""}
          </p>
          {/* One glyph, two jobs, and they are genuinely different jobs. A column that
              is always on screen needs emptying; a sheet needs dismissing, and
              dismissing must keep the selection so the reader can come back to it
              from the topbar without hunting for the marker again. */}
          <button
            className="sheet-close"
            onClick={sheet ? closeSheet : clear}
            aria-label={sheet ? "Close evidence" : "Clear evidence"}
          >
            ×
          </button>
        </header>

        {error && (
          <p data-testid="evidence-error" style={{ color: "var(--danger)" }}>
            {error}
          </p>
        )}

        {!block && !error && <p data-testid="evidence-loading">resolving…</p>}

        {block && (
          <>
            <blockquote data-testid="evidence-block" className="evidence-block">
              <Marked text={block.text} spans={selected.spans} />
            </blockquote>

            {selected.secondaryBlockId &&
              selected.secondarySpans &&
              (selected.secondaryBlockId === selected.blockId ? (
                // Both mentions share one block: paint the second span set inline in
                // the same quote rather than repeating the block's text.
                <blockquote data-testid="evidence-block-secondary" className="evidence-block">
                  <Marked text={block.text} spans={selected.secondarySpans} />
                </blockquote>
              ) : (
                secondaryBlock && (
                  <blockquote data-testid="evidence-block-secondary" className="evidence-block">
                    <Marked text={secondaryBlock.text} spans={selected.secondarySpans} />
                  </blockquote>
                )
              ))}

            {isTranscript(block.locator) && (
              // The quote-integrity trigger guarantees the span matches blocks.text;
              // nothing guarantees blocks.text matches the audio. The label states
              // that, and the deep link puts the audio one click away — the whole
              // mitigation for the ASR failure class.
              <p data-testid="asr-label" style={{ color: "var(--ink-faint)" }}>
                Machine transcription of audio — the text is what the recogniser heard,
                not a verified quote of the speech.
                {transcriptDeepLink(block.document_meta, block.locator) && (
                  <>
                    {" "}
                    <a
                      data-testid="transcript-deeplink"
                      href={transcriptDeepLink(block.document_meta, block.locator)!}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Listen at the source
                    </a>
                  </>
                )}
              </p>
            )}

            <dl className="provenance" data-testid="evidence-provenance">
              <dt>file</dt>
              <dd data-testid="prov-file">{block.original_name}</dd>
              <dt>sha256</dt>
              <dd data-testid="prov-sha" title={block.sha256}>
                {block.sha256.slice(0, 16)}…
              </dd>
              <dt>locator</dt>
              <dd data-testid="prov-locator">{describeLocator(block.locator)}</dd>
              <dt>chars</dt>
              <dd data-testid="prov-range">
                {selected.spans.map((s) => `${s.start_cp}–${s.end_cp}`).join(", ")}
              </dd>
              {block.event_ts && (
                <>
                  <dt>sent</dt>
                  <dd>{block.event_ts}</dd>
                </>
              )}
              <dt>ingested</dt>
              <dd>{block.ingested_at}</dd>
              {block.lang && (
                <>
                  <dt>language</dt>
                  <dd>{block.lang}</dd>
                </>
              )}
            </dl>

            <p>
              <a href={`/api/datasets/${datasetId}/files/${block.file_id}/original`} download>
                Download the original file
              </a>
            </p>
          </>
        )}

        {context && context.length > 0 && (
          <section data-testid="evidence-context">
            <p className="eyebrow">Conversation</p>
            {context.map((message) => (
              <p
                key={message.block_id}
                data-testid={message.is_target ? "context-target" : "context-message"}
                className={message.is_target ? "context target" : "context"}
              >
                <span className="mono" style={{ color: "var(--ink-faint)" }}>
                  {message.sent_at ?? "—"}
                </span>{" "}
                <strong>{message.sender_handle ?? "unknown"}</strong>
                <br />
                {message.text}
              </p>
            ))}
          </section>
        )}
      </aside>
    </>
  );
}

/**
 * The way back into the sheet, for the topbar.
 *
 * A citation tap opens the sheet, so this is not how evidence is normally reached — it
 * is how it is reached *again*, after dismissing it to read the rest of the answer.
 * Without it, "what did that quote say?" costs a scroll back through the answer to
 * find the marker, which is exactly the hunt the sheet exists to remove.
 *
 * In the topbar rather than the workspace tab strip because the panel is not the
 * workspace's: the search, inference and dossier routes all serve the same one
 * selection, and a control that lived in the tab strip would have to be reinvented,
 * differently, three more times. It appears only when there is something to go back
 * to, which is also what keeps it off the routes that never select anything.
 */
export function EvidenceOpener() {
  const { selected, openSheet } = useEvidence();
  const sheet = useSheetMode();
  if (!sheet || !selected) return null;
  return (
    <button
      className="evidence-open"
      data-testid="evidence-open"
      aria-haspopup="dialog"
      onClick={openSheet}
    >
      Evidence
    </button>
  );
}
